import { type TreeNode } from 'primereact/treenode';
import {
    getLabelText,
    isPlainLabelTree,
} from '../../../_utils/plain-label.utils';
import {
    type TreeNodeMaps,
    type TreeSelectSelectionMode,
    type TreeSelectValue,
} from '../models/tree-select-checkbox-selection-behavior';
import { type TreeSelectProps } from '../models/tree-select.types';
import { canonicalizeAncestorsOnlySelection } from './tree-select-checkbox-selection.utils';
import { shouldUseExternalSelectedValueLayout } from './tree-select.utils';

export interface TreeSelectSelectionFlags {
    isAncestorsOnlyCheckboxMode: boolean;
    shouldBuildNodeMaps: boolean;
    shouldUseOverflowValueTemplate: boolean;
    useExternalSelectedValueLayout: boolean;
}

interface GetTreeSelectSelectionFlagsInput {
    checkboxSelectionBehavior: TreeSelectProps['checkboxSelectionBehavior'];
    display: TreeSelectProps['display'];
    inputValueTemplate: TreeSelectProps['valueTemplate'];
    options: TreeNode[] | undefined;
    selectedValueLayout: TreeSelectProps['selectedValueLayout'];
    selectionMode: TreeSelectSelectionMode | undefined;
}

export function isAncestorsOnlyCheckboxMode(
    checkboxSelectionBehavior: TreeSelectProps['checkboxSelectionBehavior'],
    selectionMode: TreeSelectSelectionMode | undefined,
): boolean {
    return (
        checkboxSelectionBehavior === 'ancestorsOnly' &&
        selectionMode === 'checkbox'
    );
}

export function getTreeSelectSelectionFlags({
    checkboxSelectionBehavior,
    display,
    inputValueTemplate,
    options,
    selectedValueLayout,
    selectionMode,
}: GetTreeSelectSelectionFlagsInput): TreeSelectSelectionFlags {
    const ancestorsOnlyCheckboxMode = isAncestorsOnlyCheckboxMode(
        checkboxSelectionBehavior,
        selectionMode,
    );
    const externalSelectedValueLayout = shouldUseExternalSelectedValueLayout(
        selectionMode,
        selectedValueLayout,
    );
    const overflowValueTemplate =
        !inputValueTemplate &&
        !externalSelectedValueLayout &&
        display !== 'chip' &&
        isPlainLabelTree(
            options,
            (node) => getLabelText(node.label),
            (node) => node.children,
        );

    return {
        isAncestorsOnlyCheckboxMode: ancestorsOnlyCheckboxMode,
        shouldBuildNodeMaps:
            ancestorsOnlyCheckboxMode ||
            externalSelectedValueLayout ||
            overflowValueTemplate,
        shouldUseOverflowValueTemplate: overflowValueTemplate,
        useExternalSelectedValueLayout: externalSelectedValueLayout,
    };
}

export function getCanonicalTreeSelectValue(
    value: TreeSelectValue,
    nodeMaps: TreeNodeMaps | undefined,
    ancestorsOnlyCheckboxMode: boolean,
): TreeSelectValue {
    if (!ancestorsOnlyCheckboxMode || !nodeMaps) {
        return value;
    }

    return canonicalizeAncestorsOnlySelection(value, nodeMaps);
}
